import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const Profile = () => {
  const { user, updateUserLocal, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    language: 'de'
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        language: user.language || i18n.language || 'de'
      });
    }
  }, [user, i18n.language]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Sprache sofort ändern wenn ausgewählt
    if (name === 'language') {
      i18n.changeLanguage(value);
    }
  };

  const handleFileChange = (e) => {
    setPhotoFile(e.target.files[0]);
    setMessage('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      // Update profile data
      const response = await axios.put('/api/users/profile', {
        name: formData.name,
        email: formData.email,
        language: formData.language
      });

      updateUserLocal(response.data);
      setMessage(t('profile.profileUpdated'));
    } catch (error) {
      if (error.response?.data?.error?.includes('Email already in use')) {
        setError(t('profile.emailInUse'));
      } else {
        setError(error.response?.data?.error || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) {
      setError(t('profile.noPhotoUploaded'));
      return;
    }

    const formData = new FormData();
    formData.append('photo', photoFile);

    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.post('/api/users/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update only the user's photoUrl in the local state
      // The photo upload already updated the database, so no need for additional API call
      updateUserLocal({ photoUrl: response.data.photoUrl });
      setMessage(t('profile.photoUploadSuccess'));
      setPhotoFile(null);
    } catch (error) {
      if (error.response?.data?.error?.includes('Only image files')) {
        setError(t('profile.onlyImageFiles'));
      } else if (error.response?.data?.error?.includes('File too large')) {
        setError(t('profile.fileTooLarge'));
      } else {
        setError(error.response?.data?.error || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoDelete = async () => {
    setLoading(true);
    setMessage('');
    setError('');

    try {
      await axios.delete('/api/users/delete-photo');
      updateUserLocal({ photoUrl: null });
      setMessage(t('profile.photoDeleteSuccess'));
    } catch (error) {
      setError(error.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="profile-container">
      <div className="content-box">
        <h1>{t('profile.title')}</h1>

        {/* Profile Photo Section */}
        <div className="profile-photo-section">
          <h3>{t('profile.photo')}</h3>
          <div className="photo-container">
            {user?.photoUrl ? (
              <img 
                src={`http://localhost:8000${user.photoUrl}?t=${Date.now()}`} 
                alt="Profile" 
                className="profile-photo"
              />
            ) : (
              <div className="profile-photo-placeholder">
                {user?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>
          
          <div className="photo-actions">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="photo-upload"
            />
            <label htmlFor="photo-upload" className="btn btn-secondary">
              {user?.photoUrl ? t('profile.changePhoto') : t('profile.uploadPhoto')}
            </label>
            
            {photoFile && (
              <button 
                onClick={handlePhotoUpload} 
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? t('common.loading') : t('common.save')}
              </button>
            )}
            
            {user?.photoUrl && (
              <button 
                onClick={handlePhotoDelete} 
                disabled={loading}
                className="btn btn-danger"
              >
                {t('profile.deletePhoto')}
              </button>
            )}
          </div>
        </div>

        {/* Personal Information */}
        <form onSubmit={handleSubmit} className="profile-form">
          <h3>{t('profile.personalInfo')}</h3>
          
          <div className="form-group">
            <label htmlFor="name">{t('auth.name')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="language">{t('profile.language')}</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="de">{t('profile.languageGerman')}</option>
              <option value="en">{t('profile.languageEnglish')}</option>
            </select>
          </div>

          <button type="submit" disabled={loading} className="btn btn-primary btn-full">
            {loading ? t('common.loading') : t('profile.updateProfile')}
          </button>
        </form>

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}
        
        {/* FAQ Section */}
        <div className="content-box">
          <h3>{t('faq.title') || 'FAQ'}</h3>
          <p>{t('faq.subtitle') || 'Hier findest du Antworten auf die häufigsten Fragen zu togee.'}</p>
          <Link to="/faq" className="btn btn-secondary btn-full">
            {t('faq.viewFAQ') || 'FAQ anzeigen'}
          </Link>
        </div>

        {/* Logout Section */}
        <div className="content-box">
          <button onClick={handleLogout} className="btn btn-danger btn-full">
            {t('navigation.logout') || 'Abmelden'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile; 